import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Navigation from "../components/Navigation";

const AIVoiceTestScreen = () => {
  const navigate = useNavigate();
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [aiResponse, setAiResponse] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);

  const handleStartListening = () => {
    setIsListening(true);
    setTranscript("Listening...");
    // Mock voice recognition
    setTimeout(() => {
      setTranscript("Show me my portfolio performance");
      setIsListening(false);
    }, 2000);
  };

  const handleStopListening = () => {
    setIsListening(false);
  };

  const handleProcessVoice = () => {
    if (!transcript || transcript === "Listening...") return;

    setIsProcessing(true);
    // Mock AI processing
    setTimeout(() => {
      setAiResponse(
        "Your portfolio is currently valued at $12,450.67 with a total return of +11.02% over the past month. Your best performing investment is Microsoft (MSFT) with a return of +15.67%."
      );
      setIsProcessing(false);
    }, 1500);
  };

  const voiceCommands = [
    "Show me my portfolio",
    "What's the current price of AAPL?",
    "Add 10 shares of TSLA",
    "Show my transaction history",
    "What's my best performing stock?",
    "Set up a price alert for MSFT",
  ];

  return (
    <div className="screen">
      <div className="screen-header">
        <h1 className="screen-title">AI Voice Assistant</h1>
        <p className="screen-subtitle">Test voice commands and AI responses</p>
      </div>

      {/* Voice Control */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Voice Recognition</h3>
        </div>
        <div className="text-center">
          <div
            style={{
              width: "120px",
              height: "120px",
              borderRadius: "50%",
              background: isListening ? "#EF4444" : "#3B82F6",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 20px",
              cursor: "pointer",
              transition: "all 0.3s ease",
              fontSize: "2rem",
            }}
            onClick={isListening ? handleStopListening : handleStartListening}
          >
            {isListening ? "⏹️" : "🎤"}
          </div>
          <div style={{ color: "#94A3B8", marginBottom: "20px" }}>
            {isListening
              ? "Click to stop listening"
              : "Click to start listening"}
          </div>
        </div>
      </div>

      {/* Transcript */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Voice Transcript</h3>
          <button
            onClick={handleProcessVoice}
            disabled={
              !transcript || transcript === "Listening..." || isProcessing
            }
            className="btn btn-small"
          >
            {isProcessing ? "Processing..." : "Process"}
          </button>
        </div>
        <div
          style={{
            background: "#2A2A2A",
            padding: "16px",
            borderRadius: "8px",
            minHeight: "60px",
            color: transcript ? "#E5E7EB" : "#94A3B8",
          }}
        >
          {transcript || "No voice input detected"}
        </div>
      </div>

      {/* AI Response */}
      {aiResponse && (
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">AI Response</h3>
          </div>
          <div
            style={{
              background: "#2A2A2A",
              padding: "16px",
              borderRadius: "8px",
              minHeight: "60px",
              color: "#E5E7EB",
              lineHeight: "1.6",
            }}
          >
            {aiResponse}
          </div>
        </div>
      )}

      {/* Voice Commands Examples */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Example Voice Commands</h3>
        </div>
        <div className="grid-2">
          {voiceCommands.map((command, index) => (
            <div
              key={index}
              className="stock-item"
              style={{ cursor: "pointer" }}
              onClick={() => setTranscript(command)}
            >
              <div className="stock-header">
                <div>
                  <div className="stock-name">{command}</div>
                  <div className="stock-symbol">Click to try</div>
                </div>
                <span style={{ color: "#94A3B8" }}>→</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Voice Settings */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Voice Settings</h3>
        </div>
        <div className="grid-2">
          <div className="form-group">
            <label className="form-label">Voice Language</label>
            <select className="form-input">
              <option value="en-US">English (US)</option>
              <option value="en-GB">English (UK)</option>
              <option value="es-ES">Spanish</option>
              <option value="fr-FR">French</option>
              <option value="de-DE">German</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Voice Speed</label>
            <select className="form-input">
              <option value="slow">Slow</option>
              <option value="normal" selected>
                Normal
              </option>
              <option value="fast">Fast</option>
            </select>
          </div>
        </div>
        <div className="grid-2" style={{ marginTop: "20px" }}>
          <button className="btn btn-outline btn-large">
            🎵 Test Voice Output
          </button>
          <button className="btn btn-outline btn-large">
            ⚙️ Advanced Settings
          </button>
        </div>
      </div>

      {/* Voice Features */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Voice Features</h3>
        </div>
        <div className="grid-2">
          <div className="stock-item">
            <div className="stock-header">
              <div>
                <div className="stock-name">Portfolio Queries</div>
                <div className="stock-symbol">Ask about your investments</div>
              </div>
              <span style={{ color: "#22C55E" }}>✓</span>
            </div>
          </div>
          <div className="stock-item">
            <div className="stock-header">
              <div>
                <div className="stock-name">Market Data</div>
                <div className="stock-symbol">Get real-time prices</div>
              </div>
              <span style={{ color: "#22C55E" }}>✓</span>
            </div>
          </div>
          <div className="stock-item">
            <div className="stock-header">
              <div>
                <div className="stock-name">Trade Execution</div>
                <div className="stock-symbol">Place orders by voice</div>
              </div>
              <span style={{ color: "#F59E0B" }}>⚠</span>
            </div>
          </div>
          <div className="stock-item">
            <div className="stock-header">
              <div>
                <div className="stock-name">Alerts Setup</div>
                <div className="stock-symbol">Create price alerts</div>
              </div>
              <span style={{ color: "#22C55E" }}>✓</span>
            </div>
          </div>
        </div>
      </div>

      <Navigation />
    </div>
  );
};

export default AIVoiceTestScreen;
